/**
 * ErrorAlert Component
 * Displays error/warning/info alerts with optional retry and dismiss actions
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorAlertProps {
  title: string;
  message: string;
  action?: string;
  severity?: 'error' | 'warning' | 'info';
  onRetry?: () => void;
  onDismiss?: () => void;
}

export function ErrorAlert({
  title,
  message,
  action,
  severity = 'error',
  onRetry,
  onDismiss,
}: ErrorAlertProps) {
  const Icon = severity === 'error'
    ? AlertCircle
    : severity === 'warning'
    ? AlertTriangle
    : Info;

  const variant = severity === 'error'
    ? 'destructive'
    : 'default';

  return (
    <Alert variant={variant} className="mb-4">
      <Icon className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <p>{message}</p>
        {action && <p className="mt-2 text-sm font-medium">{action}</p>}
        {(onRetry || onDismiss) && (
          <div className="mt-4 flex gap-2">
            {onRetry && (
              <Button type="button" size="sm" variant="outline" onClick={onRetry}>
                Try Again
              </Button>
            )}
            {onDismiss && (
              <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
                Dismiss
              </Button>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
